import { AvoidLib, type Avoid, type Router } from "libavoid-js";

/** Libavoid docu is here: https://www.adaptagrams.org/documentation/namespaceAvoid.html */

type State = "LOADED" | "NOT_LOADED" | "LOADING";

let state: State = "NOT_LOADED";
let current: Avoid | null = null;
let reloadPromise: Promise<void> | null = null;
let lastError: unknown = null;

/** avoidGen counts lib reloads. We need this to make sure our router belongs to the current version of the library */
let avoidGen = 0;
export function getGeneration() {
  return avoidGen;
}

let router: Router | null = null;
let routerGen = -1;

let configureRouterCallback: ((Avoid: Avoid, router: Router) => void) | null = null;
let rebuildRouterCallback: ((Avoid: Avoid, router: Router) => void) | null = null;

export function onConfigureRouter(cb: (Avoid: Avoid, router: Router) => void) {
  configureRouterCallback = cb;
}
export function onRebuildRouter(cb: (Avoid: Avoid, router: Router) => void) {
  rebuildRouterCallback = cb;
}

export async function initAvoid() {
  await reload();
}

export function triggerReload() {
  if (!reloadPromise)
    reloadPromise = reload().finally(() => {
      reloadPromise = null;
    });
}

async function reload() {
  lastError = null;

  try {
    if (router && current) current.destroy?.(router);
  } catch {
    /* empty */
  }
  router = null;
  routerGen = -1;
  if (current) {
    resetAvoidSingleton();
  }
  current = null;
  try {
    state = "LOADING";
    await AvoidLib.load();
    current = AvoidLib.getInstance();
    avoidGen++;
    state = "LOADED";
  } catch (e) {
    lastError = e;
    state = "NOT_LOADED";
    throw e;
  }
}
// Hack suggested by ChatGpt "worth a try"  because AvoidLib does not allow re-inits.
// I tested it and it seems to work.
function resetAvoidSingleton() {
  try {
    const Avoid = AvoidLib.getInstance?.();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Avoid && (Avoid as any).destroy?.((window as any).__router__);
  } catch {
    /**  */
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (AvoidLib as any).avoidLib = null;
}

export function looksAbortedError(e: unknown): boolean {
  return /aborted|RuntimeError|program has already aborted|Cannot read properties/i.test(String(e));
}

export function isAvoidAlive(): boolean {
  if (!current || state === "NOT_LOADED") return false;
  try {
    const p = new current.Point(0, 0);
    current.destroy?.(p);
    return true;
  } catch (error) {
    return false;
  }
}

export function ensureAvoidAlive(): Avoid {
  if (!current || !isAvoidAlive()) {
    if (!reloadPromise) triggerReload();
    throw new Error(
      "[avoid] unavailable (reloading), state = " + state + (lastError ? `; last: ${String(lastError)}` : "")
    );
  }
  state = "LOADED";
  return current;
}
/** Lazy load of router. */
export function getRouter(): { Avoid: Avoid; router: Router } {
  const Avoid = ensureAvoidAlive();
  if (!router || routerGen !== avoidGen) {
    try {
      // destroy oldGen
      if (router) {
        Avoid.destroy?.(router);
      }
    } catch {
      /* empty */
    }
    router = new Avoid.Router(Avoid.OrthogonalRouting);
    routerGen = avoidGen;
    configureRouterCallback?.(Avoid, router);
    if (!rebuildRouterCallback) {
      throw new Error("[avoid] no rebuild callback registered");
    }
    if (avoidGen > 1) {
      rebuildRouterCallback(Avoid, router);
    }
  }
  return { Avoid, router };
}

export function withRouter<T>(fn: (Avoid: Avoid, router: Router) => T): T {
  const { Avoid, router } = getRouter();
  try {
    return fn(Avoid, router);
  } catch (e) {
    const exceptionIsHandled = handleIfAborted(e);
    if (!exceptionIsHandled) {
      throw e;
    }
  }
}

function handleIfAborted(e: unknown): boolean {
  if (looksAbortedError(e)) {
    triggerReload();
    return true;
  }
  return false;
}

export function guardAvoidCall<T>(fn: (Avoid: Avoid) => T): T {
  const Avoid = ensureAvoidAlive();
  try {
    const result = fn(Avoid);
    return result;
  } catch (e) {
    const exceptionIsHandled = handleIfAborted(e);
    if (!exceptionIsHandled) {
      throw e;
    }
    throw e;
  }
}

export function invalidateRouterNow() {
  try {
    const Avoid = current;
    if (Avoid && router) Avoid.destroy?.(router);
  } catch {
    /* empty */
  }
  router = null;
  routerGen = -1;
}
